import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useSelector } from "react-redux";
import { selectUserRole, selectCurrentUser } from "../../../core/data/slice/authSlice";
import {
  useGetNotificationByIdQuery,
  useSaveFeedbackMutation,
} from "../api/notificationApi";
import { NotificationResponseType, Feedback } from "../types/notificationResponse";
import { downloadFile } from "../api/fileUploadApi";
import { showToast } from "../../../core/utils/alert";

const NotificationView: React.FC = () => {
  const routes = all_routes;
  const { id } = useParams<{ id: string }>();
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const [feedbackMessage, setFeedbackMessage] = React.useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = React.useState(false);
  const [existingFeedback, setExistingFeedback] = React.useState<Feedback | null>(
    null
  );

  const [saveFeedback] = useSaveFeedbackMutation();

  // Fetch notification by ID using the API
  const {
    data: notificationData,
    isLoading,
    isError,
  } = useGetNotificationByIdQuery({ id: id || "" }, { skip: !id });

  // Format timestamp to IST
  const formatToIST = (timestamp: string) => {
    if (!timestamp) return "";

    // Parse the timestamp and add 5:30 hours for IST
    const date = new Date(timestamp);
    date.setTime(date.getTime() + 5.5 * 60 * 60 * 1000); // Add 5 hours and 30 minutes

    // Format to readable date and time
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get file icon based on extension
  const getFileIcon = (
    filename: string
  ): { iconClass: string; iconColor: string } => {
    const extension = filename.split(".").pop()?.toLowerCase() || "";
    let iconClass = "file";
    let iconColor = "text-secondary";

    // Determine icon and color based on extension
    if (["pdf"].includes(extension)) {
      iconClass = "file-pdf";
      iconColor = "text-danger";
    } else if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)
    ) {
      iconClass = "image";
      iconColor = "text-success";
    } else if (["doc", "docx"].includes(extension)) {
      iconClass = "file-word";
      iconColor = "text-primary";
    } else if (["xls", "xlsx", "csv"].includes(extension)) {
      iconClass = "file-excel";
      iconColor = "text-success";
    } else if (["ppt", "pptx"].includes(extension)) {
      iconClass = "file-powerpoint";
      iconColor = "text-warning";
    } else if (["zip", "rar", "7z"].includes(extension)) {
      iconClass = "file-archive";
      iconColor = "text-info";
    } else if (["mp4", "avi", "mov", "webm"].includes(extension)) {
      iconClass = "file-video";
      iconColor = "text-purple";
    } else if (["mp3", "wav", "ogg"].includes(extension)) {
      iconClass = "file-audio";
      iconColor = "text-info";
    } else if (["txt", "log", "md"].includes(extension)) {
      iconClass = "file-alt";
      iconColor = "text-secondary";
    } else {
      iconClass = "file-circle-question";
      iconColor = "text-muted";
    }

    return { iconClass, iconColor };
  };

  // Handle file download using the fileUploadApi
  const handleDownload = async (filename: string): Promise<void> => {
    try {
      // Show loading toast
      showToast({
        title: "Downloading",
        text: "File download in progress...",
        icon: "info",
      });

      // Use the downloadFile function from fileUploadApi
      await downloadFile(filename, "notifications", "document");

      // Show success toast
      showToast({
        title: "Success",
        text: "File downloaded successfully",
        icon: "success",
      });
    } catch (error: any) {
      console.error("Error downloading file:", error);

      // Show error toast
      showToast({
        title: "Error",
        text: error.message || "Failed to download file. Please try again.",
        icon: "error",
      });
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackMessage.trim() || !id) return;

    setIsSubmittingFeedback(true);
    try {
      await saveFeedback({
        notificationId: id,
        message: feedbackMessage.trim(),
      }).unwrap();

      showToast({
        title: "Success",
        text: "Feedback submitted successfully",
        icon: "success",
      });
      const feedbackObj: Feedback = {
        id: Date.now(), // Temporary ID for UI
        message: feedbackMessage.trim(),
        feedback_by: currentUser?.username || "",
        feedback_at: new Date().toISOString(),
        role: userRole || "centeradmin",
      };
      setExistingFeedback(feedbackObj);
      setFeedbackSubmitted(true);
      setFeedbackMessage("");
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      showToast({
        title: "Error",
        text: error.data?.message || "Failed to submit feedback",
        icon: "error",
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const notification: NotificationResponseType | undefined =
    notificationData?.data?.notification;

  // Check for existing feedback from this center
  useEffect(() => {
    if (notification && currentUser) {
      if (notification.has_feedback) {
        setFeedbackSubmitted(true);
      }

      const feedbackList = Array.isArray(notification.feedbacks)
        ? notification.feedbacks
        : Object.values(notification.feedbacks || {});

      // Find feedback by this user/center
      const userFeedback = feedbackList.find(
        (f) => f.id === currentUser.center_id
      );

      if (userFeedback) {
        setExistingFeedback(userFeedback);
        setFeedbackSubmitted(true);
      }
    }
  }, [notification, currentUser]);

  // Check if attachments exist and are not empty
  const hasAttachments =
    notification?.attachments &&
    notification.attachments.length > 0 &&
    notification.attachments.some((att) => att && att.trim() !== "");

  // Show loading or error states
  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !notification) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="alert alert-danger">
            Notification not found or an error occurred.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Notification</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.centerDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.activity}>Notifications</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Notification Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center">
            <Link
              to="#"
              className="btn btn-outline-primary"
              onClick={() => window.history.back()}
            >
              <i className="ti ti-arrow-left me-1"></i> Back
            </Link>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Notification Content */}
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">{notification.title}</h4>
                {notification.publishedOn && (
                  <p className="text-muted mt-2 mb-0">
                    <i className="ti ti-calendar me-1"></i>
                    {formatToIST(notification.publishedOn)}
                  </p>
                )}
              </div>
              <div className="card-body">
                <div className="notification-content mb-4">
                  {notification.content.split("\n").map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>

                {/* Attachments - Only show if attachments exist */}
                {hasAttachments && (
                  <div className="attachments border-top pt-3">
                    <h5 className="mb-3">Attachments</h5>
                    <div className="row g-2">
                      {notification.attachments
                        .filter((att) => att && att.trim() !== "")
                        .map((attachment, idx) => {
                          // Get icon information for this file
                          const { iconClass, iconColor } =
                            getFileIcon(attachment);

                          return (
                            <div className="col-md-6" key={idx}>
                              <div className="card attachment-card border">
                                <div className="card-body p-2">
                                  <div className="d-flex align-items-center">
                                    <div className="attachment-icon me-2">
                                      <i
                                        className={`fas fa-${iconClass} fa-lg ${iconColor}`}
                                      ></i>
                                    </div>
                                    <div className="attachment-info">
                                      <h6
                                        className="mb-0 text-truncate"
                                        style={{ maxWidth: "180px" }}
                                      >
                                        {attachment}
                                      </h6>
                                      <small className="text-muted">
                                        <a
                                          href="#"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleDownload(attachment);
                                          }}
                                          className="text-primary d-flex align-items-center"
                                        >
                                          <i className="ti ti-download me-1"></i>
                                          Download
                                        </a>
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                {/* Feedback Section */}
                {notification.allowFeedback &&
                  userRole !== "superadmin" &&
                  !feedbackSubmitted && (
                    <div className="feedback-section border-top mt-4 pt-3">
                      <h5 className="mb-3">Leave Feedback</h5>
                      <div className="form-group mb-3">
                        <textarea
                          className="form-control"
                          placeholder="Type your feedback here..."
                          rows={3}
                          value={feedbackMessage}
                          onChange={(e) => setFeedbackMessage(e.target.value)}
                          disabled={isSubmittingFeedback}
                        ></textarea>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={handleFeedbackSubmit}
                        disabled={isSubmittingFeedback || !feedbackMessage.trim()}
                      >
                        {isSubmittingFeedback ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                            ></span>
                            Submitting...
                          </>
                        ) : (
                          "Submit Feedback"
                        )}
                      </button>
                    </div>
                  )}

                {feedbackSubmitted && (
                  <div className="feedback-submitted-section border-top mt-4 pt-3">
                    <h5 className="mb-3">Your Feedback</h5>
                    <div className="alert alert-soft-success border shadow-sm p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="badge badge-soft-success">
                          <i className="ti ti-check me-1"></i> Submitted
                        </span>
                        {existingFeedback?.feedback_at && (
                          <small className="text-muted">
                            {new Date(existingFeedback.feedback_at).toLocaleString()}
                          </small>
                        )}
                      </div>
                      <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                        {existingFeedback?.message || "You have already submitted feedback for this notification."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationView;
